class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = theme.utils.debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.onInputChange.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    // Add event listener for price range apply button
    this.addEventListener('click', this.onPriceRangeApply.bind(this));

    // Add event listener for drawer overlay click to close drawer
    this.setupDrawerOverlayClose();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      if (searchParams === FacetFiltersForm.searchParamsPrev) return;

      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    FacetFiltersForm.renderProductPerPage();

    // Listing mód induló betöltése
    FacetFiltersForm.renderInitialListingGrid();

    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static isCollectionPage() {
    return /\/collections\/[^/]+(?:\/listing)?\/?$/.test(
      window.location.pathname
    );
  }

  static getBaseCollectionPath() {
    let path = window.location.pathname.replace(/\/+$/, '');

    if (path.endsWith('/listing')) {
      path = path.slice(0, -'/listing'.length);
    }

    return path;
  }

  static getListingPath() {
    return `${FacetFiltersForm.getBaseCollectionPath()}/listing`;
  }

  static hasActiveFilters(searchParams = '') {
    const params = new URLSearchParams(searchParams);

    for (const [key, value] of params.entries()) {
      if (key.startsWith('filter.') && value !== '') {
        return true;
      }
    }

    return false;
  }

  static buildSectionUrl(pathname, sectionId, searchParams = '') {
    const url = new URL(pathname, window.location.origin);
    const params = new URLSearchParams(searchParams);

    params.set('section_id', sectionId);
    url.search = params.toString();

    return `${url.pathname}${url.search}`;
  }

  static renderInitialListingGrid() {
    const render = () => {
      if (!FacetFiltersForm.isCollectionPage()) return;

      const searchParams = window.location.search.slice(1);

      // Ha már van aktív filter, nem listing módban indulunk.
      if (FacetFiltersForm.hasActiveFilters(searchParams)) return;

      const productGrid = document.getElementById('product-grid');

      if (!productGrid || !productGrid.dataset.id) return;

      /*
       * Megőrizzük az oldal eredeti termékszámait.
       *
       * A témában több ProductCount ID is található,
       * ezért az összeset eltároljuk.
       */
      const productCountSnapshots = Array.from(
        document.querySelectorAll('#ProductCount, #ProductCountMobile')
      ).map((element) => ({
        element,
        html: element.innerHTML
      }));

      const listingUrl = FacetFiltersForm.buildSectionUrl(
        FacetFiltersForm.getListingPath(),
        productGrid.dataset.id,
        searchParams
      );

      fetch(listingUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          return response.text();
        })
        .then((html) => {
          /*
           * Lehet, hogy a kérés futása közben a vásárló
           * bekapcsolt egy szűrőt.
           *
           * Ilyenkor a normál filtered gridnek kell nyernie.
           */
          if (
            FacetFiltersForm.hasActiveFilters(
              window.location.search.slice(1)
            )
          ) {
            return;
          }

          /*
           * Csak a listing grid + pagination cserélődik.
           */
          FacetFiltersForm.renderProductGridContainer(html, true);

          /*
           * A normál collection termékszámait visszaállítjuk.
           *
           * A /listing kérés nem írhatja át a collection
           * teljes termékszámát.
           */
          productCountSnapshots.forEach(({ element, html }) => {
            if (element.isConnected) {
              element.innerHTML = html;
            }
          });

          /*
           * Egy következő render frame-ben is biztosítjuk,
           * hogy egy másik theme inicializálás ne írja felül.
           */
          requestAnimationFrame(() => {
            if (
              FacetFiltersForm.hasActiveFilters(
                window.location.search.slice(1)
              )
            ) {
              return;
            }

            productCountSnapshots.forEach(({ element, html }) => {
              if (element.isConnected) {
                element.innerHTML = html;
              }
            });
          });

          if (typeof initializeScrollAnimationTrigger === 'function') {
            initializeScrollAnimationTrigger();
          }
        })
        .catch((error) => {
          console.error(
            'Error loading listing collection grid:',
            error
          );
        });
    };

    /*
     * Nem indítjuk el a listing DOM-cserét addig,
     * amíg a teljes oldal és a theme nem töltődött be.
     */
    if (document.readyState === 'complete') {
      render();
    } else {
      window.addEventListener('load', render, { once: true });
    }
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();

    const countContainer = document.getElementById('ProductCount');
    const countContainerMobile =
      document.getElementById('ProductCountMobile');

    const loadingSpinners = document.querySelectorAll(
      '.product-count .loading__spinner, .product-grid-container .loading__spinner'
    );

    loadingSpinners.forEach((spinner) =>
      spinner.classList.remove('hidden')
    );

    const productGridContainer =
      document.getElementById('ProductGridContainer');

    if (productGridContainer) {
      const collection =
        productGridContainer.querySelector('.collection');

      if (collection) {
        collection.classList.add('loading');
      }
    }

    if (countContainer) {
      countContainer.classList.add('loading');
    }

    if (countContainerMobile) {
      countContainerMobile.classList.add('loading');
    }

    const isCollectionPage = FacetFiltersForm.isCollectionPage();

    const useListingGrid =
      isCollectionPage &&
      !FacetFiltersForm.hasActiveFilters(searchParams);

    const requestPath = isCollectionPage
      ? FacetFiltersForm.getBaseCollectionPath()
      : window.location.pathname;

    sections.forEach((section) => {
      const url = FacetFiltersForm.buildSectionUrl(
        requestPath,
        section.section,
        searchParams
      );

      const gridUrl = useListingGrid
        ? FacetFiltersForm.buildSectionUrl(
            FacetFiltersForm.getListingPath(),
            section.section,
            searchParams
          )
        : url;

      const filterDataUrl = (element) =>
        element.url === url && element.gridUrl === gridUrl;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(
            filterDataUrl,
            event
          )
        : FacetFiltersForm.renderSectionFromFetch(
            url,
            gridUrl,
            event,
            useListingGrid
          );
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }
  }

  static renderSectionFromFetch(
    url,
    gridUrl,
    event,
    listingMode = false
  ) {
    const sectionRequest = fetch(url).then((response) =>
      response.text()
    );

    const gridRequest =
      gridUrl === url
        ? sectionRequest
        : fetch(gridUrl).then((response) => response.text());

    Promise.all([sectionRequest, gridRequest])
      .then(([html, gridHtml]) => {
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            gridHtml,
            url,
            gridUrl,
            listingMode
          }
        ];

        FacetFiltersForm.renderFilters(html, event);

        FacetFiltersForm.renderProductGridContainer(
          gridHtml,
          listingMode
        );

        FacetFiltersForm.renderProductCount(html);

        // DIGILOOP - added html to renderProductPerPage for re-render
        FacetFiltersForm.renderProductPerPage(html);

        FacetFiltersForm.clickGridView(html);

        if (
          typeof initializeScrollAnimationTrigger === 'function'
        ) {
          initializeScrollAnimationTrigger(html.innerHTML);
        }
      })
      .catch((error) => {
        console.error(
          'Error rendering collection filters:',
          error
        );
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const cachedData =
      FacetFiltersForm.filterData.find(filterDataUrl);

    if (!cachedData) return;

    const html = cachedData.html;
    const gridHtml = cachedData.gridHtml || html;
    const listingMode = cachedData.listingMode === true;

    FacetFiltersForm.renderFilters(html, event);

    FacetFiltersForm.renderProductGridContainer(
      gridHtml,
      listingMode
    );

    FacetFiltersForm.renderProductCount(html);

    // DIGILOOP - added html to renderProductPerPage for re-render
    FacetFiltersForm.renderProductPerPage(html);

    FacetFiltersForm.clickGridView(html);

    if (
      typeof initializeScrollAnimationTrigger === 'function'
    ) {
      initializeScrollAnimationTrigger(html.innerHTML);
    }
  }

  static renderProductGridContainer(
    html,
    listingMode = false
  ) {
    const parsedHTML = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const container =
      document.getElementById('ProductGridContainer');

    if (!container) return;

    if (
      listingMode &&
      FacetFiltersForm.isCollectionPage()
    ) {
      /*
       * LISTING MODE
       *
       * A /listing válaszból csak:
       *
       * - #ResultsList
       * - pagination
       *
       * kerül át.
       *
       * A normál collection ProductCount és egyéb
       * collection adatok érintetlenek maradnak.
       */

      const fetchedResults =
        parsedHTML.getElementById('ResultsList');

      const currentResults =
        container.querySelector('#ResultsList');

      if (!fetchedResults || !currentResults) return;

      currentResults.outerHTML =
        fetchedResults.outerHTML;

      const productGrid = container.querySelector(
        '#ResultsList .product-grid'
      );

      if (productGrid) {
        productGrid.dataset.listingMode = 'true';

        productGrid.dataset.listingPath =
          FacetFiltersForm.getListingPath();
      }

      /*
       * Listing pagination frissítése.
       */
      const fetchedPagination =
        parsedHTML.querySelector(
          '#ProductGridContainer .pagination-wrapper'
        );

      const currentPagination =
        container.querySelector('.pagination-wrapper');

      if (
        currentPagination &&
        fetchedPagination
      ) {
        currentPagination.outerHTML =
          fetchedPagination.outerHTML;
      } else if (
        currentPagination &&
        !fetchedPagination
      ) {
        currentPagination.remove();
      } else if (
        !currentPagination &&
        fetchedPagination
      ) {
        const collectionContainer =
          container.querySelector('.collection');

        if (collectionContainer) {
          collectionContainer.insertAdjacentHTML(
            'beforeend',
            fetchedPagination.outerHTML
          );
        }
      }

      /*
       * Számozott pagination esetén a /listing
       * útvonal ne jelenjen meg a vásárlónak.
       */
      container
        .querySelectorAll('.pagination a[href]')
        .forEach((link) => {
          const linkUrl = new URL(
            link.getAttribute('href'),
            window.location.origin
          );

          const listingPath =
            FacetFiltersForm.getListingPath().replace(
              /\/+$/,
              ''
            );

          if (
            linkUrl.pathname.replace(/\/+$/, '') ===
            listingPath
          ) {
            linkUrl.pathname =
              FacetFiltersForm.getBaseCollectionPath();

            link.setAttribute(
              'href',
              `${linkUrl.pathname}${linkUrl.search}${linkUrl.hash}`
            );
          }
        });
    } else {
      /*
       * NORMÁL / FILTEREZETT MODE
       *
       * Itt az eredeti témaműködés marad:
       * a teljes ProductGridContainer cserélődik.
       */

      const fetchedContainer =
        parsedHTML.getElementById(
          'ProductGridContainer'
        );

      if (!fetchedContainer) return;

      container.innerHTML =
        fetchedContainer.innerHTML;

      const productGrid =
        container.querySelector('.product-grid');

      if (productGrid) {
        delete productGrid.dataset.listingMode;
        delete productGrid.dataset.listingPath;
      }
    }

    /*
     * Listing módban nem cseréljük le a teljes .collection elemet,
     * ezért manuálisan le kell venni róla a loading állapotot.
     *
     * Normál/filterezett módban sem árt biztosítani,
     * hogy ne maradjon beragadt loading állapot.
     */
    const collectionElement =
      container.querySelector('.collection');

    if (collectionElement) {
      collectionElement.classList.remove('loading');
    }

    /*
     * Az esetlegesen aktív grid loading spinnereket is
     * visszarejtjük.
     */
    container
      .querySelectorAll('.loading__spinner')
      .forEach((spinner) => {
        spinner.classList.add('hidden');
      });

    container
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add(
          'scroll-trigger--cancel'
        );
      });

    // Rebind 'show more swatches' buttons after grid re-render
    if (
      typeof initMoreSwatchButtons === 'function'
    ) {
      initMoreSwatchButtons(container || document);
    }
  }

  static clickGridView(event) {
    document
      .querySelectorAll('.button--grid-view')
      .forEach((button) => {
        button.addEventListener(
          'click',
          this.changeLayoutGrid.bind(this)
        );
      });
  }

  static changeLayoutGrid(event) {
    const gridView =
      event.currentTarget.dataset.grid;

    document
      .querySelectorAll('.button--grid-view')
      .forEach((button) => {
        button.classList.add('cursor-pointer');
        button.classList.remove('active');
      });

    event.currentTarget.classList.remove(
      'cursor-pointer'
    );

    event.currentTarget.classList.add('active');

    const productGrid = document.querySelector(
      '.product-grid-container .product-grid'
    );

    if (productGrid) {
      productGrid.setAttribute(
        'data-view',
        gridView
      );
    }
  }

  static renderProductPerPage(html) {
    const productGrid = document.querySelector(
      '.product-grid-container .product-grid'
    );

    // DIGILOOP - added re-render
    if (html) {
      const parsedHTML = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const selector = '.products-per-page';

      if (!parsedHTML.querySelector(selector)) return;

      const currentSelector =
        document.querySelector(selector);

      if (currentSelector) {
        currentSelector.innerHTML =
          parsedHTML.querySelector(selector).innerHTML;
      }
    }

    document
      .querySelectorAll('input[name="per_page"]')
      .forEach(function (input) {
        input.addEventListener('change', function () {
          const detailsList =
            input.closest('.details__list');

          const currentSelected = detailsList
            ? detailsList.querySelector('.selected')
            : null;

          if (currentSelected) {
            currentSelected.classList.remove(
              'selected',
              'pointer-events-none'
            );
          }

          const currentItem = input.closest(
            '.facet-filters__sort_item'
          );

          if (currentItem) {
            currentItem.classList.add(
              'selected',
              'pointer-events-none'
            );
          }

          const labelText = input.closest('details')
            ? input
                .closest('details')
                .querySelector('.label-text')
            : null;

          if (labelText) {
            labelText.innerHTML = this.value;
          }

          if (detailsList) {
            detailsList
              .querySelectorAll(
                'input[name="per_page"]'
              )
              .forEach((other) => {
                if (other !== input) {
                  other.checked = false;
                }
              });

            detailsList
              .querySelectorAll(
                '.facet-filters__sort_item'
              )
              .forEach((li) => {
                if (li !== currentItem) {
                  li.classList.remove(
                    'selected',
                    'pointer-events-none'
                  );
                }
              });
          }

          var data = new URLSearchParams();

          data.append(
            'attributes[pagination]',
            this.value
          );

          fetch('/cart/update.js', {
            method: 'POST',
            body: data,
            headers: {
              'Content-Type':
                'application/x-www-form-urlencoded'
            }
          })
            .then((response) => response.text())
            .finally(() => {
              // DIGILOOP - removed to prevent duplicated call via
              // FacetFiltersForm.renderPage(window.location.search.slice(1), null, false);
            });
        });
      });

    if (productGrid) {
      const gridViewButton =
        document.querySelector(
          '.button--grid-view.active'
        );

      if (gridViewButton) {
        productGrid.setAttribute(
          'data-view',
          gridViewButton.dataset.grid
        );
      }
    }
  }

  static renderProductCount(html) {
    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const sourceCount =
      parsedHTML.getElementById('ProductCount');

    if (!sourceCount) return;

    const count = sourceCount.innerHTML;

    const container =
      document.getElementById('ProductCount');

    const containerMobile =
      document.getElementById(
        'ProductCountMobile'
      );

    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }

    if (containerMobile) {
      containerMobile.innerHTML = count;
      containerMobile.classList.remove(
        'loading'
      );
    }

    const loadingSpinners =
      document.querySelectorAll(
        '.product-count .loading__spinner'
      );

    loadingSpinners.forEach((spinner) =>
      spinner.classList.add('hidden')
    );
  }

  static renderFilters(html, event) {
    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const facetDetailsElementsFromFetch =
      parsedHTML.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
      );

    const facetDetailsElementsFromDom =
      document.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
      );

    // Remove facets that are no longer returned from the server
    Array.from(
      facetDetailsElementsFromDom
    ).forEach((currentElement) => {
      if (
        !Array.from(
          facetDetailsElementsFromFetch
        ).some(
          ({ id }) =>
            currentElement.id === id
        )
      ) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event
        ? event.target.closest('.js-filter')
        : undefined;

      return jsFilter
        ? element.id === jsFilter.id
        : false;
    };

    const facetsToRender = Array.from(
      facetDetailsElementsFromFetch
    ).filter(
      (element) => !matchesId(element)
    );

    const countsToRender = Array.from(
      facetDetailsElementsFromFetch
    ).find(matchesId);

    facetsToRender.forEach(
      (elementToRender, index) => {
        const currentElement =
          document.getElementById(
            elementToRender.id
          );

        if (currentElement) {
          document.getElementById(
            elementToRender.id
          ).innerHTML =
            elementToRender.innerHTML;
        } else {
          if (index > 0) {
            const {
              className:
                previousElementClassName,
              id: previousElementId
            } = facetsToRender[index - 1];

            if (
              elementToRender.className ===
              previousElementClassName
            ) {
              document
                .getElementById(
                  previousElementId
                )
                .after(elementToRender);

              return;
            }
          }

          if (elementToRender.parentElement) {
            const parentFilter =
              document.querySelector(
                `#${elementToRender.parentElement.id} .js-filter`
              );

            if (parentFilter) {
              parentFilter.before(
                elementToRender
              );
            }
          }
        }
      }
    );

    FacetFiltersForm.renderActiveFacets(
      parsedHTML
    );

    FacetFiltersForm.renderAdditionalElements(
      parsedHTML
    );

    if (countsToRender && event) {
      const closestFilter =
        event.target.closest('.js-filter');

      if (closestFilter) {
        const closestJSFilterID =
          closestFilter.id;

        if (closestJSFilterID) {
          FacetFiltersForm.renderCounts(
            countsToRender,
            closestFilter
          );

          FacetFiltersForm.renderMobileCounts(
            countsToRender,
            document.getElementById(
              closestJSFilterID
            )
          );

          const newFacetDetailsElement =
            document.getElementById(
              closestJSFilterID
            );

          if (!newFacetDetailsElement) return;

          const newElementSelector =
            newFacetDetailsElement.classList.contains(
              'mobile-facets__details'
            )
              ? '.mobile-facets__close-button'
              : '.facets__summary';

          const newElementToActivate =
            newFacetDetailsElement.querySelector(
              newElementSelector
            );

          const isTextInput =
            event.target.getAttribute('type') ===
            'text';

          if (
            newElementToActivate &&
            !isTextInput
          ) {
            newElementToActivate.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '#FacetsWrapperDesktop',
      '.active-facets-desktop'
    ];

    activeFacetElementSelectors.forEach(
      (selector) => {
        const activeFacetsElement =
          html.querySelector(selector);

        if (!activeFacetsElement) return;

        const target =
          document.querySelector(selector);

        if (target) {
          target.innerHTML =
            activeFacetsElement.innerHTML;
        }
      }
    );

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting'
    ];

    mobileElementSelectors.forEach(
      (selector) => {
        const source =
          html.querySelector(selector);

        const target =
          document.querySelector(selector);

        if (!source || !target) return;

        target.innerHTML = source.innerHTML;
      }
    );

    const mobileForm =
      document.getElementById(
        'FacetFiltersFormMobile'
      );

    if (mobileForm) {
      const drawer =
        mobileForm.closest('menu-drawer');

      if (
        drawer &&
        typeof drawer.bindEvents === 'function'
      ) {
        drawer.bindEvents();
      }
    }

    const facetFiltersForm =
      document.querySelector(
        'facet-filters-form'
      );

    if (facetFiltersForm) {
      facetFiltersForm.setupDrawerOverlayClose();
    }
  }

  static renderCounts(source, target) {
    const targetSummary =
      target.querySelector(
        '.facets__summary'
      );

    const sourceSummary =
      source.querySelector(
        '.facets__summary'
      );

    if (
      sourceSummary &&
      targetSummary
    ) {
      targetSummary.outerHTML =
        sourceSummary.outerHTML;
    }

    const targetHeaderElement =
      target.querySelector(
        '.facets__header'
      );

    const sourceHeaderElement =
      source.querySelector(
        '.facets__header'
      );

    if (
      sourceHeaderElement &&
      targetHeaderElement
    ) {
      targetHeaderElement.outerHTML =
        sourceHeaderElement.outerHTML;
    }

    const targetWrapElement =
      target.querySelector(
        '.facets-wrap'
      );

    const sourceWrapElement =
      source.querySelector(
        '.facets-wrap'
      );

    if (
      sourceWrapElement &&
      targetWrapElement
    ) {
      const isShowingMore = Boolean(
        target.querySelector(
          'show-more-button .label-show-more.hidden'
        )
      );

      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(
            '.facets__item.hidden'
          )
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace(
              'hidden',
              'show-more-item'
            )
          );
      }

      targetWrapElement.outerHTML =
        sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    if (!target) return;

    const targetFacetsList =
      target.querySelector(
        '.mobile-facets__list'
      );

    const sourceFacetsList =
      source.querySelector(
        '.mobile-facets__list'
      );

    if (
      sourceFacetsList &&
      targetFacetsList
    ) {
      targetFacetsList.outerHTML =
        sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const pathname =
      FacetFiltersForm.isCollectionPage()
        ? FacetFiltersForm.getBaseCollectionPath()
        : window.location.pathname;

    history.pushState(
      { searchParams },
      '',
      `${pathname}${
        searchParams
          ? '?'.concat(searchParams)
          : ''
      }`
    );
  }

  static getSections() {
    const productGrid =
      document.getElementById(
        'product-grid'
      );

    if (
      !productGrid ||
      !productGrid.dataset.id
    ) {
      return [];
    }

    return [
      {
        section: productGrid.dataset.id
      }
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);

    return new URLSearchParams(
      formData
    ).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(
      searchParams,
      event
    );
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const sortFilterForms =
      document.querySelectorAll(
        'facet-filters-form form'
      );

    if (
      event.srcElement.className ==
      'mobile-facets__checkbox'
    ) {
      const searchParams =
        this.createSearchParams(
          event.target.closest('form')
        );

      this.onSubmitForm(
        searchParams,
        event
      );
    } else {
      const forms = [];

      const currentForm =
        event.target.closest('form');

      if (!currentForm) return;

      const isMobile =
        currentForm.id ===
        'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === 'FacetSortForm' ||
            form.id ===
              'FacetFiltersForm' ||
            form.id ===
              'FacetSortDrawerForm'
          ) {
            forms.push(
              this.createSearchParams(form)
            );
          }
        } else if (
          form.id ===
          'FacetFiltersFormMobile'
        ) {
          forms.push(
            this.createSearchParams(form)
          );
        }
      });

      this.onSubmitForm(
        forms.join('&'),
        event
      );
    }
  }

  onInputChange(event) {
    const isPriceRangeInput =
      event.target.classList.contains(
        'filter__price_change'
      ) ||
      event.target.classList.contains(
        'filter__price_number'
      );

    if (!isPriceRangeInput) {
      this.debouncedOnSubmit(event);
    }
  }

  onPriceRangeApply(event) {
    if (
      event.target.closest(
        '.facets-price__apply button'
      )
    ) {
      event.preventDefault();

      const form =
        this.querySelector('form');

      if (form) {
        const searchParams =
          this.createSearchParams(form);

        this.onSubmitForm(
          searchParams,
          event
        );
      }
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf('?') ==
      -1
        ? ''
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf(
              '?'
            ) + 1
          );

    FacetFiltersForm.renderPage(url);

    if (window.innerWidth <= 1024) {
      document
        .querySelectorAll(
          '.mobile-facets__details'
        )
        .forEach((item) => {
          item.removeAttribute('style');
          item.removeAttribute('open');
        });
    }
  }

  setupDrawerOverlayClose() {
    const drawerOverlay =
      this.querySelector(
        '[data-drawer-overlay]'
      );

    if (drawerOverlay) {
      drawerOverlay.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.closeDrawer();
        }
      );
    }
  }

  closeDrawer() {
    const closeButton =
      this.querySelector(
        '[data-close-drawer]'
      );

    if (closeButton) {
      closeButton.click();

      document.body.classList.remove(
        'overflow-hidden-mobile'
      );
    } else {
      const menuDrawer =
        this.closest('menu-drawer');

      if (menuDrawer) {
        const details =
          menuDrawer.querySelector(
            'details'
          );

        if (
          details &&
          details.hasAttribute('open')
        ) {
          details.removeAttribute('open');

          document.body.classList.remove(
            'overflow-hidden-mobile'
          );
        }
      }
    }
  }
}

FacetFiltersForm.filterData = [];

FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);

FacetFiltersForm.searchParamsPrev =
  window.location.search.slice(1);

if (
  !customElements.get(
    'facet-filters-form'
  )
) {
  customElements.define(
    'facet-filters-form',
    FacetFiltersForm
  );
}

FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.rangeSliderPrice();

    this.querySelectorAll('input').forEach(
      (element) => {
        element.addEventListener(
          'change',
          this.onRangeChange.bind(this)
        );

        element.addEventListener(
          'keydown',
          this.onKeyDown.bind(this)
        );
      }
    );

    this.setMinAndMaxValues();

    const numberS = this.querySelectorAll(
      '.filter__price_change'
    );

    let value1 = numberS[0].value;
    let value2 = numberS[1].value;

    this.updateDisplay(value1, value2);
  }

  onRangeChange(event) {
    // this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();

    // Note: Form submission is now handled by the apply button click
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;

    if (!event.key.match(pattern)) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const inputs =
      this.querySelectorAll('input');

    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (minInput.value === '') {
      maxInput.setAttribute(
        'data-min',
        0
      );
    }

    if (maxInput.value === '') {
      minInput.setAttribute(
        'data-max',
        maxInput.getAttribute('data-max')
      );
    }
  }

  rangeSliderPrice() {
    let rangeS =
        this.querySelectorAll(
          '.filter__price_change'
        ),
      numberS =
        this.querySelectorAll(
          '.filter__price_number'
        ),
      isFireFox =
        typeof InstallTrigger !==
        'undefined',
      lowerSlider = rangeS[0],
      upperSlider = rangeS[1];

    rangeS.forEach((element) => {
      element.oninput = () => {
        let slide1 = Math.floor(
            rangeS[0].value
          ),
          slide2 = Math.ceil(
            rangeS[1].value
          ),
          slide = Math.round(
            (Number(
              this.querySelector(
                '.filter__price_change'
              ).max
            ) /
              100) *
              (72 /
                (this.offsetWidth / 100))
          );

        if (
          slide1 >
          slide2 - slide
        ) {
          if (
            slide2 != upperSlider.max
          ) {
            upperSlider.value =
              slide1 + slide;

            numberS[0].value =
              lowerSlider.value;

            numberS[1].value =
              slide1 + slide;

            if (
              slide2 ==
              upperSlider.max
            ) {
              lowerSlider.value =
                parseInt(
                  upperSlider.max
                ) - slide;

              numberS[0].value =
                parseInt(
                  upperSlider.max
                ) - slide;
            }
          }
        }

        if (
          slide2 <
          slide1 + slide
        ) {
          if (
            slide1 ==
            lowerSlider.min
          ) {
            return;
          }

          lowerSlider.value =
            slide2 - slide;

          numberS[0].value =
            slide2 - slide;

          numberS[1].value =
            upperSlider.value;

          if (
            slide1 ==
            lowerSlider.min
          ) {
            upperSlider.value =
              slide;

            numberS[1].value =
              slide;
          }
        }

        if (
          slide2 <
            slide1 + slide &&
          slide2 ==
            upperSlider.max
        ) {
          return;
        }

        numberS[0].value =
          slide1;

        numberS[1].value =
          slide2;

        if (!isFireFox) {
          this.updateDisplay(
            numberS[0].value,
            numberS[1].value
          );
        }
      };
    });

    numberS.forEach((element) => {
      element.oninput = () => {
        var number1 = parseFloat(
            numberS[0].placeholder
          ),
          checkValue1 =
            number1 != number1,
          number2 = parseFloat(
            numberS[1].placeholder
          ),
          checkValue2 =
            number2 != number2;

        if (!checkValue1) {
          rangeS[0].placeholder =
            number1;
        }

        if (!checkValue2) {
          rangeS[1].placeholder =
            number2;
        }

        if (number1 > number2) {
          if (!isFireFox) {
            this.updateDisplay(
              number2,
              number1
            );
          }
        } else {
          if (!isFireFox) {
            this.updateDisplay(
              number1,
              number2
            );
          }
        }
      };
    });
  }

  updateDisplay(value1, value2) {
    const priceRange =
      this.querySelector(
        '.facets-price__range'
      );

    const priceInput =
      this.querySelector(
        '.filter__price_change'
      );

    if (!priceRange || !priceInput) {
      return;
    }

    const max = priceInput.max;
    const width =
      priceRange.clientWidth;

    priceRange.style.setProperty(
      '--left-space',
      (parseInt(value1) /
        parseInt(max)) *
        width +
        'px'
    );

    priceRange.style.setProperty(
      '--right-space',
      width -
        (parseInt(value2) /
          parseInt(max)) *
          width +
        'px'
    );
  }
}

if (
  !customElements.get('price-range')
) {
  customElements.define(
    'price-range',
    PriceRange
  );
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink =
      this.querySelector('a');

    if (!facetLink) return;

    facetLink.setAttribute(
      'role',
      'button'
    );

    facetLink.addEventListener(
      'click',
      this.closeFilter.bind(this)
    );

    facetLink.addEventListener(
      'keyup',
      (event) => {
        event.preventDefault();

        if (
          event.code.toUpperCase() ===
          'SPACE'
        ) {
          this.closeFilter(event);
        }
      }
    );
  }

  closeFilter(event) {
    event.preventDefault();

    const form =
      this.closest(
        'facet-filters-form'
      ) ||
      document.querySelector(
        'facet-filters-form'
      );

    if (form) {
      form.onActiveFilterClick(event);
    }
  }
}

if (
  !customElements.get('facet-remove')
) {
  customElements.define(
    'facet-remove',
    FacetRemove
  );
}